import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { Edit } from "@mui/icons-material";
import { Box, IconButton, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { ColDef, RowClassRules } from "ag-grid-community";
import type {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	MenuItemDef,
} from "ag-grid-enterprise";
import { type MenuOptionType, useContextMenuMESF } from "@dexteel/mesf-core";
import type { IncentiveRow, DayTotals } from "../models/IncentiveRow";
import { useIncThresholds } from "../hooks/useIncThresholds";
import { IncPercentPill } from "./IncPercentPill";
import { getZoneColor, ROW_TINTS, SURFACE, TEXT } from "../constants/colors";
import { SHIFT_CONFIG_COLORS } from "@/shared/components/KPICard";

/** Fixed week layout: 7 days × 2 shifts = 14 rows, Sun → Sat */
const WEEKDAYS = [
	"Sunday",
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
] as const;
const SHIFTS_PER_DAY = 2;

/** Index-based day label */
const getDayLabel = (rowIndex: number): string =>
	WEEKDAYS[Math.floor(rowIndex / SHIFTS_PER_DAY)] ?? "";

/** Formats "2026-05-28" → "05/28" without timezone shift */
const fmtShortDate = (iso: string): string => {
	const [, m, d] = iso.substring(0, 10).split("-");
	return `${m}/${d}`;
};

/** First row of each day pair gets row span 2, second gets 1 */
const isFirstOfDay = (rowIndex: number): boolean =>
	rowIndex % SHIFTS_PER_DAY === 0;
const getDayRowSpan = (rowIndex: number): number =>
	isFirstOfDay(rowIndex) ? SHIFTS_PER_DAY : 1;

type Props = {
	dailyRows: IncentiveRow[];
	dayTotals: DayTotals[];
	canEdit?: boolean;
	onEdit?: (row: IncentiveRow) => void;
};

export const DailySummaryGrid = ({
	dailyRows,
	dayTotals,
	canEdit,
	onEdit,
}: Props) => {
	const { defaultColDef } = useGridDefinitions({});
	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { thresholds: incThresholds } = useIncThresholds();

	const getMenuOptions = useCallback(
		(data: IncentiveRow | null): MenuOptionType[] => {
			if (!data || !canEdit || data.IsFutureShift || data.TargetTons == null)
				return [];

			return [
				{
					name: "Edit",
					key: "edit_incentive",
					onClick: () => onEdit?.(data),
					icon: <Edit fontSize="small" color="primary" />,
				},
			];
		},
		[canEdit, onEdit],
	);

	const getMenuOptionsRef = useRef(getMenuOptions);
	getMenuOptionsRef.current = getMenuOptions;

	useEffect(() => {
		registerConfig({
			id: "incentiveContext",
			getOptions: (data: unknown) => {
				if (!data || typeof data !== "object" || !("ShiftId" in data))
					return [];
				return getMenuOptionsRef.current(data as IncentiveRow);
			},
		});
	}, []);

	const getContextMenuItems = useCallback(
		(
			params: GetContextMenuItemsParams<IncentiveRow>,
		): (DefaultMenuItem | MenuItemDef)[] => {
			// Suppress context menu on spanned cells (Day / Avg Inc%) — ambiguous which shift
			const header = params.column?.getColDef()?.headerName;
			if (header === "Day" || header === "Avg Inc%") {
				return [];
			}

			const data = params.node?.data;

			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}

			showContextMenu(
				params.event as unknown as React.MouseEvent,
				data,
				"incentiveContext",
			);

			return [];
		},
		[showContextMenu],
	);

	const totalsMap = useMemo(() => {
		const map = new Map<string, DayTotals>();
		for (const dt of dayTotals) {
			map.set(dt.ProductionDate, dt);
		}
		return map;
	}, [dayTotals]);

	/** Set of ProductionDates that contain a current shift (for spanned cell tinting) */
	const currentDates = useMemo(() => {
		const set = new Set<string>();
		for (const r of dailyRows) {
			if (r.IsCurrentShift) set.add(r.ProductionDate);
		}
		return set;
	}, [dailyRows]);

	/** Set of ProductionDates where ALL shifts are future (for avg pill coloring) */
	const allFutureDates = useMemo(() => {
		const dateShifts = new Map<string, boolean[]>();
		for (const r of dailyRows) {
			if (!dateShifts.has(r.ProductionDate))
				dateShifts.set(r.ProductionDate, []);
			dateShifts.get(r.ProductionDate)!.push(r.IsFutureShift);
		}
		const set = new Set<string>();
		for (const [date, flags] of dateShifts) {
			if (flags.every(Boolean)) set.add(date);
		}
		return set;
	}, [dailyRows]);

	/** Set of ProductionDates where any shift has a target override (for spanned cell tinting) */
	const overrideDates = useMemo(() => {
		const set = new Set<string>();
		if (!canEdit) return set;
		for (const r of dailyRows) {
			if (r.TargetTonsOverride != null) set.add(r.ProductionDate);
		}
		return set;
	}, [dailyRows, canEdit]);

	const columnDefs = useMemo(
		(): ColDef<IncentiveRow>[] => [
			{
				headerName: "Day",
				width: 110,
				valueGetter: (params) =>
					params.node?.rowIndex != null
						? getDayLabel(params.node.rowIndex)
						: "",
				rowSpan: (params) =>
					params.node?.rowIndex != null
						? getDayRowSpan(params.node.rowIndex)
						: 1,
				cellClassRules: {
					"ag-cell-row-span": (params) =>
						params.node?.rowIndex != null && isFirstOfDay(params.node.rowIndex),
					"incentive-cell-day-border": (params) =>
						params.node?.rowIndex != null && isFirstOfDay(params.node.rowIndex),
					"incentive-row-current": (params) =>
						params.node?.rowIndex != null &&
						isFirstOfDay(params.node.rowIndex) &&
						!!params.data?.ProductionDate &&
						currentDates.has(params.data.ProductionDate),
					"incentive-row-override": (params) =>
						params.node?.rowIndex != null &&
						isFirstOfDay(params.node.rowIndex) &&
						!!params.data?.ProductionDate &&
						overrideDates.has(params.data.ProductionDate),
				},
				cellRenderer: (params: {
					data: IncentiveRow;
					node: { rowIndex: number | null };
				}) => {
					if (
						params.node?.rowIndex == null ||
						!isFirstOfDay(params.node.rowIndex)
					)
						return null;
					return (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								lineHeight: 1.3,
							}}
						>
							<span style={{ fontWeight: 600 }}>
								{getDayLabel(params.node.rowIndex)}
							</span>
							<span style={{ fontSize: "0.7rem", color: "#9e9e9e" }}>
								{fmtShortDate(params.data.ProductionDate)}
							</span>
						</div>
					);
				},
				cellStyle: (params) =>
					params.node?.rowIndex != null && isFirstOfDay(params.node.rowIndex)
						? { display: "flex", alignItems: "center" }
						: undefined,
			},
			{
				headerName: "Crew",
				field: "Crew",
				width: 60,
				cellStyle: { textAlign: "center" } as const,
			},
			{
				headerName: "Shift",
				field: "Shift",
				width: 60,
				cellStyle: { textAlign: "center" } as const,
			},
			{
				headerName: "Target Tons",
				field: "DisplayedTarget",
				width: 85,
				cellStyle: { textAlign: "center" } as const,
				cellRenderer: (params: { data: IncentiveRow }) => {
					const row = params.data;
					return (
						<span style={{ position: "relative" }}>
							{row.DisplayedTarget ? row.DisplayedTarget.toFixed(0) : "—"}
							{canEdit && row.TargetTonsOverride != null && (
								<span
									style={{
										position: "absolute",
										left: "100%",
										marginLeft: 2,
										fontSize: "0.5rem",
										fontWeight: 700,
										color: "#f57c00",
										whiteSpace: "nowrap",
									}}
								>
									OVR
								</span>
							)}
						</span>
					);
				},
			},
			{
				headerName: "Finished Tons",
				field: "DisplayedFinished",
				width: 90,
				cellStyle: { textAlign: "right" } as const,
				cellRenderer: (params: { data: IncentiveRow }) => {
					const row = params.data;
					if (row.IsFutureShift) {
						return <span style={{ color: TEXT.disabled }}>—</span>;
					}
					return (
						<span>
							{row.DisplayedFinished != null
								? row.DisplayedFinished.toFixed(0)
								: "—"}
						</span>
					);
				},
			},
			{
				headerName: "Adj.",
				field: "Adjustment",
				width: 55,
				cellStyle: { textAlign: "right" } as const,
				valueFormatter: (params) => {
					const v = params.value;
					if (v === 0 || v == null) return "—";
					return v > 0 ? `+${v}` : String(v);
				},
			},
			{
				headerName: "Hrs",
				field: "HoursWorked",
				width: 50,
				cellStyle: { textAlign: "right" } as const,
				valueFormatter: (params) => (params.value ? String(params.value) : "—"),
			},
			{
				headerName: "Inc%",
				field: "IncPercent",
				width: 90,
				cellRenderer: (params: {
					value: number | null;
					data: IncentiveRow;
				}) => (
					<IncPercentPill
						value={params.value}
						thresholds={incThresholds}
						isFuture={params.data?.IsFutureShift}
					/>
				),
				cellStyle: {
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				} as const,
			},
			{
				headerName: "Avg Inc%",
				width: 100,
				rowSpan: (params) =>
					params.node?.rowIndex != null
						? getDayRowSpan(params.node.rowIndex)
						: 1,
				cellClassRules: {
					"ag-cell-row-span": (params) =>
						params.node?.rowIndex != null && isFirstOfDay(params.node.rowIndex),
					"incentive-cell-day-border": (params) =>
						params.node?.rowIndex != null && isFirstOfDay(params.node.rowIndex),
					"incentive-row-current": (params) =>
						params.node?.rowIndex != null &&
						isFirstOfDay(params.node.rowIndex) &&
						!!params.data?.ProductionDate &&
						currentDates.has(params.data.ProductionDate),
					"incentive-row-override": (params) =>
						params.node?.rowIndex != null &&
						isFirstOfDay(params.node.rowIndex) &&
						!!params.data?.ProductionDate &&
						overrideDates.has(params.data.ProductionDate),
				},
				cellRenderer: (params: {
					data: IncentiveRow;
					node: { rowIndex: number | null };
				}) => {
					if (
						params.node?.rowIndex == null ||
						!isFirstOfDay(params.node.rowIndex)
					)
						return null;
					const t = totalsMap.get(params.data.ProductionDate);
					return (
						<IncPercentPill
							value={t?.AvgIncPercent ?? null}
							thresholds={incThresholds}
							isFuture={allFutureDates.has(params.data.ProductionDate)}
						/>
					);
				},
				cellStyle: {
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				} as const,
			},
			...(canEdit
				? [
						{
							headerName: "",
							width: 50,
							sortable: false,
							filter: false,
							resizable: false,
							cellClass: "incentive-cell-edit",
							cellRenderer: (params: { data: IncentiveRow }) => {
								const row = params.data;
								if (row.IsFutureShift || row.TargetTons == null) return null;
								return (
									<IconButton
										size="small"
										title="Edit shift override"
										onClick={() => onEdit?.(row)}
									>
										<Edit fontSize="inherit" color="primary" />
									</IconButton>
								);
							},
							cellStyle: {
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							} as const,
						} satisfies ColDef<IncentiveRow>,
					]
				: []),
		],
		[totalsMap, canEdit, onEdit, incThresholds, overrideDates, allFutureDates],
	);

	const rowClassRules = useMemo<RowClassRules<IncentiveRow>>(
		() => ({
			"incentive-row-override": (params) =>
				canEdit === true && params.data?.TargetTonsOverride != null,
			"incentive-row-current": (params) => params.data?.IsCurrentShift === true,
		}),
		[canEdit],
	);

	return (
		<Paper
			elevation={2}
			sx={{
				height: "100%",
				width: "100%",
				display: "flex",
				flexDirection: "column",
				"& .incentive-row-override": {
					backgroundColor: `${ROW_TINTS.override} !important`,
				},
				"& .incentive-row-current": {
					backgroundColor: `${ROW_TINTS.current} !important`,
				},
				"& .ag-cell-row-span:not(.incentive-row-current)": {
					backgroundColor: "inherit",
				},
				"& .incentive-cell-day-border": {
					borderBottom: "1px solid var(--ag-border-color) !important",
				},
				"& .incentive-cell-edit": {
					backgroundColor: "#fff !important",
				},
			}}
		>
			<Box
				sx={{
					px: 2,
					py: 1,
					borderBottom: "1px solid",
					borderColor: "divider",
					display: "flex",
					alignItems: "baseline",
					justifyContent: "space-between",
				}}
			>
				<Typography sx={{ fontSize: "1rem", fontWeight: 500 }}>
					Daily Summary
				</Typography>
			</Box>
			<Box sx={{ flex: 1, minHeight: 0 }}>
				<AgGridReact<IncentiveRow>
					rowData={dailyRows}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					suppressRowTransform={true}
					rowHeight={34}
					headerHeight={34}
					animateRows
					getRowId={(params) => String(params.data.ShiftId)}
					rowClassRules={rowClassRules}
					domLayout="normal"
					suppressCellFocus
					getContextMenuItems={getContextMenuItems}
				/>
			</Box>
			<Box
				sx={{
					px: 2,
					py: 1.5,
					borderTop: "1px solid",
					borderColor: "divider",
					display: "flex",
					flexDirection: "column",
					gap: 0.75,
					fontSize: "0.75rem",
					color: "text.secondary",
				}}
			>
				{canEdit && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Box
							sx={{
								width: 14,
								height: 14,
								borderRadius: "2px",
								backgroundColor: ROW_TINTS.override,
								border: `1px solid ${SURFACE.border}`,
								flexShrink: 0,
							}}
						/>
						<span>Target override applied</span>
					</Box>
				)}
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Box
						sx={{
							width: 14,
							height: 14,
							borderRadius: "2px",
							backgroundColor: ROW_TINTS.current,
							border: `1px solid ${SURFACE.border}`,
							flexShrink: 0,
						}}
					/>
					<span>Current shift in progress</span>
				</Box>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.75,
						mt: 0.5,
						flexWrap: "wrap",
					}}
				>
					<Typography
						sx={{ fontSize: "0.75rem", fontWeight: 600, color: "text.primary" }}
					>
						Inc%:
					</Typography>
					{[
						{
							label: "Hard",
							color: SHIFT_CONFIG_COLORS.greenHard,
							value: incThresholds.GreenHard,
						},
						{
							label: "Normal",
							color: SHIFT_CONFIG_COLORS.greenNormal,
							value: incThresholds.GreenNormal,
						},
						{
							label: "Soft",
							color: SHIFT_CONFIG_COLORS.greenSoft,
							value: incThresholds.GreenSoft,
						},
						{
							label: "Yellow",
							color: SHIFT_CONFIG_COLORS.yellow,
							value: incThresholds.Yellow,
						},
						{
							label: "Orange",
							color: SHIFT_CONFIG_COLORS.orange,
							value: incThresholds.Orange,
						},
						{
							label: "Red",
							color: SHIFT_CONFIG_COLORS.red,
							value: incThresholds.Red,
						},
					].map((zone) => (
						<Box
							key={zone.label}
							sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
						>
							<Box
								sx={{
									width: 14,
									height: 14,
									borderRadius: "2px",
									backgroundColor: zone.color,
									border: `1px solid ${SURFACE.border}`,
									flexShrink: 0,
								}}
							/>
							<span>{`≥ ${zone.value}%`}</span>
						</Box>
					))}
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						<Box
							sx={{
								width: 14,
								height: 14,
								borderRadius: "2px",
								backgroundColor: "#e0e0e0",
								border: `1px solid ${SURFACE.border}`,
								flexShrink: 0,
							}}
						/>
						<span>No data</span>
					</Box>
				</Box>
			</Box>
		</Paper>
	);
};
